import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";

const CommonButton = ({ 
  label, 
  title, 
  onClick, 
  disabled = false, 
  symbol = false,
  variant = "primary", // "primary", "success", "purple", "danger", "warning", "tab", "small"
  hoverBoxShadow,
  defaultBoxShadow,
  className = "",
  loading = false,
  isSelected = false, // For tab variant
  size = "default", // "default", "small", "large"
  bold = false, // New prop for bold text
}) => {
  
  // Default style that applies to all buttons (as per your original code)
  const defaultStyle = {
    color: "white",
    border: "none",
    borderRadius: "10px",
    padding: "8px 12px",
    margin: "0 3px",
    cursor: disabled || loading ? "not-allowed" : "pointer",
    fontSize: "10px",
    fontWeight: "600", // Default is semi-bold
    letterSpacing: "0.5px",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    minWidth: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: disabled || loading ? 0.6 : 1,
  };

  // Size overrides - only modify specific properties
  const sizeOverrides = {
    default: {}, // No overrides for default
    small: {
      padding: "4px 8px",
      fontSize: "11px",
      height: "28px",
      minWidth: "auto",
    },
    large: {
      padding: "12px 20px",
      fontSize: "14px",
      height: "44px",
      minWidth: "48px",
    }
  };

  // Base style = default + size overrides
  const baseStyle = {
    ...defaultStyle,
    ...sizeOverrides[size],
  };

  // Variant configurations
  const variants = {
    primary: {
      backgroundColor: "#4f46e5",
      backgroundImage: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
      defaultBoxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
      hoverBoxShadow: "0 4px 12px rgba(79, 70, 229, 0.4)",
      color: "white",
    },
    success: {
      backgroundColor: "#10b981",
      backgroundImage: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      defaultBoxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)",
      hoverBoxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
      color: "white",
    },
    purple: {
      backgroundColor: "#8b5cf6",
      backgroundImage: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      defaultBoxShadow: "0 2px 4px rgba(139, 92, 246, 0.2)",
      hoverBoxShadow: "0 4px 12px rgba(139, 92, 246, 0.4)",
      color: "white",
    },
    danger: {
      backgroundColor: "#ef4444",
      backgroundImage: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      defaultBoxShadow: "0 2px 4px rgba(239, 68, 68, 0.2)",
      hoverBoxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
      color: "white",
    },
    warning: {
      backgroundColor: "#f59e0b",
      backgroundImage: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      defaultBoxShadow: "0 2px 4px rgba(245, 158, 11, 0.2)",
      hoverBoxShadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
      color: "white",
    },
    tab: {
      backgroundColor: "transparent",
      backgroundImage: "none",
      color: "#666",
      border: "1px solid #d1d5db",
      defaultBoxShadow: "none",
      hoverBoxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      borderRadius: "4px",
      fontWeight: "bold", // Bold for tab variant
    },
    tabSelected: {
      backgroundColor: "#4f46e5",
      backgroundImage: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
      color: "white",
      border: "1px solid #4f46e5",
      defaultBoxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
      hoverBoxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
      borderRadius: "4px",
      fontWeight: "bold", // Bold for selected tab variant too
    },
    smallAction: {
      backgroundColor: "#28a745",
      backgroundImage: "linear-gradient(135deg, #28a745 0%, #218838 100%)",
      color: "white",
      defaultBoxShadow: "0 1px 2px rgba(40, 167, 69, 0.2)",
      hoverBoxShadow: "0 2px 4px rgba(40, 167, 69, 0.4)",
      borderRadius: "4px",
      fontSize: "11px",
      padding: "4px 8px",
      height: "28px",
      minWidth: "auto",
    },
    arrow: {
      backgroundColor: "transparent",
      backgroundImage: "none",
      color: "#007bff",
      border: "none",
      defaultBoxShadow: "none",
      hoverBoxShadow: "none",
      padding: "2px 4px",
      minWidth: "auto",
      height: "auto",
      fontSize: "14px",
      fontWeight: "normal", // Arrow doesn't need to be bold
    },
    custom: {
      backgroundColor: "inherit",
      backgroundImage: "none",
    }
  };

  // Determine which variant to use
  let selectedVariant = variants[variant] || variants.primary;
  
  // For tab variant, check if selected
  if (variant === "tab" && isSelected) {
    selectedVariant = variants.tabSelected;
  }

  // Build the final button style
  const buttonStyle = {
    ...baseStyle,
    backgroundColor: selectedVariant.backgroundColor,
    backgroundImage: disabled || loading ? "none" : selectedVariant.backgroundImage,
    boxShadow: defaultBoxShadow || selectedVariant.defaultBoxShadow,
    color: selectedVariant.color,
    border: selectedVariant.border || "none",
    fontWeight: bold ? "bold" : selectedVariant.fontWeight || "600",
    // Override with variant-specific styles if they exist
    ...(selectedVariant.padding && { padding: selectedVariant.padding }),
    ...(selectedVariant.fontSize && { fontSize: selectedVariant.fontSize }),
    ...(selectedVariant.height && { height: selectedVariant.height }),
    ...(selectedVariant.minWidth && { minWidth: selectedVariant.minWidth }),
    ...(selectedVariant.fontWeight && { fontWeight: selectedVariant.fontWeight }),
    ...(selectedVariant.borderRadius && { borderRadius: selectedVariant.borderRadius }),
  };

  // Determine box shadows (use custom props or variant defaults)
  const finalHoverBoxShadow = hoverBoxShadow || selectedVariant.hoverBoxShadow;
  const finalDefaultBoxShadow = defaultBoxShadow || selectedVariant.defaultBoxShadow;

  // Special handling for arrow buttons
  const isArrowButton = variant === "arrow";

  // Render loading spinner if needed
  const renderContent = () => {
    if (loading) {
      return (
        <>
          <div style={{
            width: "12px",
            height: "12px",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            borderTop: "2px solid white",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            marginRight: label || symbol ? "8px" : "0"
          }} />
          {label}
        </>
      );
    }

    return (
      <>
        {symbol && (
          <FontAwesomeIcon 
            icon={symbol} 
            style={{ 
              fontSize: size === "small" ? "10px" : "12px",
              marginRight: label ? "4px" : "0",
              fontWeight: "normal" // Icons typically shouldn't be bold
            }} 
          />
        )}
        {label}
      </>
    );
  };

  const handleMouseEnter = (e) => {
    if (!disabled && !loading) {
      if (isArrowButton) {
        e.target.style.color = "#0056b3";
        e.target.style.transform = "scale(1.2)";
      } else {
        e.target.style.transform = "translateY(-2px) scale(1.05)";
        e.target.style.boxShadow = finalHoverBoxShadow;
      }
    }
  };

  const handleMouseLeave = (e) => {
    if (!disabled && !loading) {
      if (isArrowButton) {
        e.target.style.color = "#007bff";
        e.target.style.transform = "scale(1)";
      } else {
        e.target.style.transform = "translateY(0) scale(1)";
        e.target.style.boxShadow = finalDefaultBoxShadow;
      }
    }
  };

  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled || loading}
      style={buttonStyle}
      className={`common-button ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {renderContent()}
    </button>
  );
};

// PropTypes for better development experience
CommonButton.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  symbol: PropTypes.any,
  variant: PropTypes.oneOf([
    'primary', 'success', 'purple', 'danger', 'warning', 
    'tab', 'tabSelected', 'smallAction', 'arrow', 'custom'
  ]),
  hoverBoxShadow: PropTypes.string,
  defaultBoxShadow: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  isSelected: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small', 'large']),
  bold: PropTypes.bool, // New prop
};

// Default props
CommonButton.defaultProps = {
  variant: 'primary',
  disabled: false,
  loading: false,
  className: "",
  isSelected: false,
  size: 'default',
  bold: false,
};

export default CommonButton;